import { Instant } from "@js-joda/core";
import React from "react";
import useInstantCountdown from "../../../hooks/useInstantCountdown";
import { RaffleParticipant } from "../Raffle.types";
import useDynamicWinnerAnimation from "./useDynamicRaffleAnimation";

const SPIN_FOR_SECONDS = 20;
const MIN_SPEED = 1;
const MAX_SPEED = 18;

const useDynamicWinnerSelector = (params: {
  participants: RaffleParticipant[];
  spinPayload: {
    active: boolean;
    winnerParticipantIndex: number;
  } | null;
  raffleEndsAt: Instant;
  onWinAnimationEnd: () => void;
  onSpinIteration: () => void;
}) => {
  const isActive = params.spinPayload !== null && params.spinPayload.active;
  const [animationSpeed, setAnimationSpeed] = React.useState(!isActive ? 1 : MAX_SPEED);
  const spinningEndsAt = React.useMemo(
    () => params.raffleEndsAt.plusSeconds(SPIN_FOR_SECONDS),
    [params.raffleEndsAt]
  );
  const { totalSeconds: timeLeft } = useInstantCountdown({ targetInstant: spinningEndsAt });

  React.useEffect(() => {
    if (!isActive) return;
    setAnimationSpeed(Math.min(MAX_SPEED, Math.max(MIN_SPEED, timeLeft / 2)));
  }, [timeLeft, isActive]);

  const [currentParticipantIndex, setCurrentParticipantIndex] = React.useState(0);
  const currentParticipant = params.participants[currentParticipantIndex];
  const animationDuration = 2000 / animationSpeed;
  const hasWinner = isActive && timeLeft === 0;

  const transformY = useDynamicWinnerAnimation({
    duration: animationDuration / 1000,
    onRepeat: () => {
      params.onSpinIteration();

      if (hasWinner && params.spinPayload !== null) {
        setCurrentParticipantIndex(params.spinPayload.winnerParticipantIndex);
        return true;
      }

      setCurrentParticipantIndex((prev) => (prev + 1) % params.participants.length);
      return false;
    },
    onWinner: () => {
      params.onWinAnimationEnd();
    },
  });

  return { transformY, currentParticipant };
};

export default useDynamicWinnerSelector;
